// Single home for "who can see which page". The registry's visibility rules
// are evaluated here for every surface: global search, the sitemap, the
// sidebar, breadcrumbs/related chips and the help drawer.
//
// Two access functions with deliberately different semantics:
//   getEntryAccess - can this user reach/search the page at all
//   getNavAccess   - should the sidebar show it (nav-only overrides apply,
//                    and tier locks are SHOWN to managerial teams rather
//                    than hidden, matching NavItem's historic behaviour)

// Teams that see a lock icon on tier-gated sidebar items instead of having
// them hidden (mirrors the list in sidebar/NavItem.jsx).
const UPGRADE_VISIBLE_TEAMS = [
  'Event Planning',
  'Commerical Training',
  'Governance',
  'Manager',
  'Supervisor',
  'Personnel',
  'Make Ready',
  'Fleet',
  'Finance',
]

export const buildNavContext = ({ user, tenantInfo, orgSettings }) => {
  const teams = user?.employee?.teams ?? []
  const isSiteAdmin = !!user?.security?.isSiteAdmin
  return {
    user,
    teams,
    isSiteAdmin,
    isSubcontractor: !!user?.subcontractor && !user?.employee,
    isEmployee: !!user?.employee,
    tier: Number(tenantInfo?.subscriptionTier ?? 1),
    cadAccess: user?.employee?.cadAccess,
    ptsAccess: user?.employee?.ptsAccess ?? [],
    // orgSettings is optional; check() functions must tolerate it missing so
    // callers that only have user + tenantInfo still work.
    orgSettings,
    hasTeam: (team) => isSiteAdmin || teams.includes(team),
  }
}

// Eligibility ignoring the subscription tier. Order matters and mirrors the
// old getSearchablePages(): subcontractor accounts only see entries opted in
// via `subcontractor`, a check() replaces the teams rule, and no rule at all
// means every employee.
const isEligible = (visibility, ctx) => {
  const v = visibility ?? {}
  if (ctx.isSubcontractor) return !!v.subcontractor
  if (v.check) return !!v.check(ctx)
  if (v.teams) {
    return ctx.isSiteAdmin || v.teams.some((team) => ctx.teams.includes(team))
  }
  return true
}

export const canSeeUpgradeLocks = (ctx) =>
  ctx.isSiteAdmin || UPGRADE_VISIBLE_TEAMS.some((t) => ctx.teams.includes(t))

// 'visible' | 'locked' | 'hidden'. Search and the sitemap treat 'locked' as
// hidden; the sidebar shows it with a lock icon to canSeeUpgradeLocks users.
export const getEntryAccess = (entry, ctx) => {
  const v = entry.visibility ?? {}
  if (!isEligible(v, ctx)) return 'hidden'
  if (v.requiredTier && ctx.tier < v.requiredTier) return 'locked'
  return 'visible'
}

// Sidebar-only access. entry.nav may override the page's own rules:
//   nav.check        replaces teams/check (e.g. Medicine Administration is
//                    reachable by everyone but only listed for non-Governance
//                    staff when the org hasn't disabled the form)
//   nav.requiredTier a tier gate that applies to the sidebar listing only
export const getNavAccess = (entry, ctx) => {
  if (!entry.nav) return 'hidden'
  const v = entry.visibility ?? {}
  const eligible = entry.nav.check
    ? !!entry.nav.check(ctx)
    : isEligible(v, ctx)
  if (!eligible) return 'hidden'
  const requiredTier = entry.nav.requiredTier ?? v.requiredTier
  if (requiredTier && ctx.tier < requiredTier) {
    return canSeeUpgradeLocks(ctx) ? 'locked' : 'hidden'
  }
  return 'visible'
}
